import { api, ApiError, type BarcodeProduct, type Item, type Location } from "../api";
import * as clientDb from "../db/clientDb";
import { createLocationPicker } from "./locationPicker";
import { createPhotoPicker } from "./photoPicker";
import { showSheet } from "./modal";
import { openScannerOverlay } from "./scannerOverlay";
import { msg } from "../messages";
import type { ScanAddDraft } from "../scanDraft";
import { resolveImageSrc } from "../utils/images";
import { formatItemPickerLabel } from "../utils/itemPickerLabel";
import { findSimilarItems } from "../utils/similarItems";
import { todayDateInputValue, toDateInputValue } from "../utils/dates";
import { setSheetError } from "../utils/sheetFormError";
import {
    applyTallyCountAnimation,
    clampTallyQuantity,
    commitTallyCount,
    ensureTallyStructure,
    type TallySlideDirection,
    updateMinusButton,
    wireEditableTallyCount,
} from "./tallyControl";
import { closeAnimatedSheet, mountAnimatedSheet } from "../utils/sheetAnimation";

export interface ItemSheetOptions {
    mode: "add" | "edit";
    item?: Item;
    itemId?: number;
    draft?: ScanAddDraft | null;
    locations?: Location[];
    categories?: string[];
    onSaved?: (item: Item) => void | Promise<void>;
    onDeleted?: (item: Item) => void;
    onClose?: () => void;
    onExistingBarcode?: (item: Item) => void;
}

/** @deprecated Use showItemSheet */
export function openItemEditSheet(
    options: Omit<ItemSheetOptions, "mode"> & { item: Item },
): () => void {
    return showItemSheet({ ...options, mode: "edit" });
}

export function showItemSheet(options: ItemSheetOptions): () => void {
    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";

    const sheet = document.createElement("div");
    sheet.className = "item-sheet";

    const header = document.createElement("header");
    header.className = "item-sheet-header";

    const title = document.createElement("h2");
    title.className = "item-sheet-title";
    title.textContent = options.mode === "add" ? "Log Item" : "Loading…";

    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.className = "item-sheet-dismiss";
    dismissBtn.setAttribute("aria-label", "Close");
    dismissBtn.textContent = "×";

    header.appendChild(title);
    header.appendChild(dismissBtn);

    const body = document.createElement("div");
    body.className = "item-sheet-body";

    sheet.appendChild(header);
    sheet.appendChild(body);
    overlay.appendChild(sheet);

    let closed = false;

    function close(): void {
        if (closed) return;
        closed = true;
        closeAnimatedSheet(overlay, () => options.onClose?.());
    }

    dismissBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
    });

    mountAnimatedSheet(overlay);
    void initSheet(body, title, options, close);
    return close;
}

async function initSheet(
    body: HTMLElement,
    titleEl: HTMLElement,
    options: ItemSheetOptions,
    close: () => void,
): Promise<void> {
    body.innerHTML = `<div class="loading">${msg("add/loading")}</div>`;

    try {
        const [locations, categories, item] = await Promise.all([
            options.locations?.length ? Promise.resolve(options.locations) : api.locations.list(),
            options.categories?.length
                ? Promise.resolve(options.categories)
                : api.items.categories(),
            resolveItem(options),
        ]);

        if (options.mode === "edit" && item && !item.image_path) {
            const pending = await clientDb.getPendingImageForRecord("items", item.id);
            if (pending) {
                item.image_path = URL.createObjectURL(pending.file);
            }
        }

        if (options.mode === "edit" && item) {
            titleEl.textContent = `Edit: ${item.name}`;
        }

        body.innerHTML = "";
        const form = document.createElement("form");
        form.className = "item-sheet-form";
        body.appendChild(form);

        mountItemSheetForm(form, {
            mode: options.mode,
            item,
            draft: options.draft ?? null,
            locations,
            categories,
            onSaved: async (saved) => {
                close();
                await options.onSaved?.(saved);
            },
            onEditExisting: (existingItem) => {
                close();
                options.onExistingBarcode?.(existingItem);
            },
            onDeleted: options.onDeleted
                ? (deletedItem) => {
                      close();
                      options.onDeleted!(deletedItem);
                  }
                : undefined,
        });
    } catch {
        body.innerHTML = `<p class="pantry-empty">${msg("add/locations-error")}</p>`;
    }
}

async function resolveItem(options: ItemSheetOptions): Promise<Item | null> {
    if (options.mode === "add") return null;
    if (options.item) return options.item;
    if (options.itemId !== undefined) return (await api.items.get(options.itemId)) ?? null;
    return null;
}

interface MountFormOptions {
    mode: "add" | "edit";
    item: Item | null;
    draft: ScanAddDraft | null;
    locations: Location[];
    categories: string[];
    onSaved: (item: Item) => void | Promise<void>;
    onEditExisting: (item: Item) => void;
    onDeleted?: (item: Item) => void;
}

function showSimilarItemPrompt(
    similar: Item[],
    onSaveAnyway: () => void,
    onEdit: (item: Item) => void,
    onCancel: () => void,
): () => void {
    const content = document.createElement("div");
    content.className = "barcode-prompt";

    const message = document.createElement("p");
    message.textContent =
        similar.length === 1
            ? "An item with very similar attributes already exists."
            : "Items with very similar attributes already exist.";
    content.appendChild(message);

    const list = document.createElement("div");
    list.className = "barcode-prompt-actions";
    for (const it of similar) {
        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "btn btn-secondary btn-block";
        editBtn.textContent = formatItemPickerLabel(it, similar);
        editBtn.addEventListener("click", () => {
            close();
            onEdit(it);
        });
        list.appendChild(editBtn);
    }
    content.appendChild(list);

    const actions = document.createElement("div");
    actions.className = "barcode-prompt-actions";

    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "btn btn-primary btn-block";
    saveBtn.textContent = "Save anyway";
    saveBtn.addEventListener("click", () => {
        close();
        onSaveAnyway();
    });
    actions.appendChild(saveBtn);

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn btn-secondary btn-block";
    cancelBtn.textContent = "Cancel";
    cancelBtn.addEventListener("click", () => {
        close();
        onCancel();
    });
    actions.appendChild(cancelBtn);

    content.appendChild(actions);

    let close: () => void;
    close = showSheet("Possible duplicate", content, onCancel, { dismissible: true });
    return close;
}

function mountItemSheetForm(form: HTMLFormElement, options: MountFormOptions): void {
    const { mode, item, draft, locations, categories, onSaved, onEditExisting, onDeleted } = options;
    const minQty = mode === "add" ? 1 : 0;
    let quantity =
        mode === "edit" && item ? Math.max(0, item.quantity) : Math.max(1, draft ? 1 : 1);

    const nameVal = item?.name ?? draft?.name ?? "";
    const brandVal = item?.brand ?? draft?.brand ?? "";
    const categoryVal = item?.category ?? draft?.category ?? "";
    const barcodeVal = item?.barcode ?? draft?.barcode ?? "";
    const expiryVal = item?.expiry_date ?? "";
    const addedVal =
        mode === "edit" && item
            ? toDateInputValue(item.created_at) || todayDateInputValue()
            : todayDateInputValue();
    const notesVal = item?.notes ?? "";
    let imageSrc: string | null = item?.image_path ?? draft?.image_path ?? null;

    form.innerHTML = `
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-name">Name</label>
      <input id="sheet-item-name" class="ledger-input" type="text" required value="${escapeAttr(nameVal)}" autocomplete="off">
    </div>
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-barcode">Barcode</label>
      <div class="barcode-row">
        <input id="sheet-item-barcode" class="ledger-input" type="text" inputmode="numeric" value="${escapeAttr(barcodeVal)}" autocomplete="off">
        <button type="button" class="barcode-scan-btn" aria-label="Scan barcode"><span class="material-symbols-outlined">photo_camera</span></button>
      </div>
      <div class="barcode-autofill is-hidden" id="sheet-barcode-autofill">
        <button type="button" class="chip barcode-autofill-chip" aria-label="Autofill from barcode">
          <span class="material-symbols-outlined">auto_fix</span>
          <span class="chip-text">Autofill product info</span>
        </button>
      </div>
    </div>
    <div class="ledger-field">
      <span class="ledger-label">Zone</span>
      <div id="sheet-item-location"></div>
    </div>
    <div class="ledger-field ledger-field--inline">
      <span class="ledger-label">Quantity</span>
      <div class="manifest-tally" id="sheet-qty-tally">
        <button type="button" class="tally-btn minus" aria-label="Decrease quantity">−</button>
        <span class="tally-count" id="sheet-qty-value">${quantity}</span>
        <button type="button" class="tally-btn plus" aria-label="Increase quantity">+</button>
      </div>
    </div>
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-brand">Brand</label>
      <input id="sheet-item-brand" class="ledger-input" type="text" value="${escapeAttr(brandVal)}" autocomplete="off">
    </div>
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-category">Category</label>
      <input id="sheet-item-category" class="ledger-input" type="text" list="sheet-item-categories" value="${escapeAttr(categoryVal)}" autocomplete="off">
      ${categories.length ? `<datalist id="sheet-item-categories">${categories.map((c) => `<option value="${escapeAttr(c)}">`).join("")}</datalist>` : ""}
    </div>
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-expiry">Expiry date</label>
      <input id="sheet-item-expiry" class="ledger-input" type="date" value="${escapeAttr(expiryVal)}">
    </div>
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-added">Date added</label>
      <input id="sheet-item-added" class="ledger-input" type="date" value="${escapeAttr(addedVal)}">
    </div>
    <div class="ledger-field">
      <label class="ledger-label" for="sheet-item-notes">Notes</label>
      <textarea id="sheet-item-notes" class="ledger-textarea">${escapeHtml(notesVal)}</textarea>
    </div>
    <div class="ledger-field">
      <span class="ledger-label">Photo</span>
      <div id="sheet-item-photo"></div>
    </div>
    <button type="submit" class="item-sheet-submit">${mode === "add" ? "LOG ITEM" : "UPDATE LEDGER"}</button>
  `;

    const qtyValue = form.querySelector("#sheet-qty-value") as HTMLElement;
    const minusBtn = form.querySelector(".tally-btn.minus") as HTMLButtonElement;
    const nameInput = form.querySelector("#sheet-item-name") as HTMLInputElement;
    const barcodeInput = form.querySelector("#sheet-item-barcode") as HTMLInputElement;
    const brandInput = form.querySelector("#sheet-item-brand") as HTMLInputElement;
    const categoryInput = form.querySelector("#sheet-item-category") as HTMLInputElement;
    const autofillRow = form.querySelector("#sheet-barcode-autofill") as HTMLElement;
    const autofillChip = form.querySelector(".barcode-autofill-chip") as HTMLButtonElement | null;
    ensureTallyStructure(qtyValue, quantity);

    function looksLikeBarcode(value: string): boolean {
        return /^\d{6,}$/.test(value.trim());
    }

    const chipIcon = autofillChip?.querySelector(".material-symbols-outlined") as HTMLElement | null;
    const chipText = autofillChip?.querySelector(".chip-text") as HTMLElement | null;
    let prescanProduct: BarcodeProduct | null = null;
    let prescanTimer: number | null = null;
    let prescanInFlight = false;
    let prescanUpc = "";

    function setAutofillLoading(): void {
        if (!autofillChip || !chipIcon || !chipText) return;
        autofillRow.classList.remove("is-hidden");
        autofillChip.disabled = true;
        autofillChip.classList.add("is-loading");
        autofillChip.classList.remove("is-matched");
        chipIcon.textContent = "progress_activity";
        chipText.textContent = "Looking up product…";
    }

    function setAutofillFound(product: BarcodeProduct): void {
        if (!autofillChip || !chipIcon || !chipText) return;
        autofillRow.classList.remove("is-hidden");
        autofillChip.disabled = false;
        autofillChip.classList.remove("is-loading", "is-matched");
        chipIcon.textContent = "auto_fix";
        chipText.textContent = `Autofill: ${product.name || "product"}`;
        autofillChip.title = product.name || "";
    }

    function setAutofillNotFound(): void {
        if (!autofillChip || !chipIcon || !chipText) return;
        autofillRow.classList.remove("is-hidden");
        autofillChip.disabled = true;
        autofillChip.classList.remove("is-loading", "is-matched");
        chipIcon.textContent = "info";
        chipText.textContent = "No product found";
        autofillChip.title = "Open Food Facts has no data for this barcode";
    }

    function setAutofillMatched(product: BarcodeProduct): void {
        if (!autofillChip || !chipIcon || !chipText) return;
        autofillRow.classList.remove("is-hidden");
        autofillChip.disabled = true;
        autofillChip.classList.remove("is-loading");
        autofillChip.classList.add("is-matched");
        chipIcon.textContent = "auto_fix";
        chipText.textContent = `Autofill: ${product.name || "product"}`;
        autofillChip.title = product.name || "";
    }

    function hideAutofillChip(): void {
        autofillRow.classList.add("is-hidden");
    }

    function refreshAutofillChip(): void {
        if (!autofillChip || !chipIcon || !chipText) return;

        const upc = barcodeInput.value.trim();
        if (!looksLikeBarcode(upc) || prescanUpc !== upc) {
            hideAutofillChip();
            return;
        }

        if (prescanProduct) {
            if (fieldsMatchProduct(prescanProduct)) setAutofillMatched(prescanProduct);
            else setAutofillFound(prescanProduct);
            return;
        }

        if (prescanTimer || prescanInFlight) {
            if (barcodeInput.matches(":focus")) {
                hideAutofillChip();
                return;
            }
            setAutofillLoading();
            return;
        }

        setAutofillNotFound();
    }

    async function runPrescan(upc: string): Promise<void> {
        prescanInFlight = true;
        let product: BarcodeProduct | null = null;
        try {
            product = await api.barcode.lookup(upc);
        } catch {
            /* lookup failed */
        }

        if (prescanUpc === upc) {
            prescanInFlight = false;
        }

        const currentUpc = barcodeInput.value.trim();
        if (!looksLikeBarcode(currentUpc) || currentUpc !== upc) {
            refreshAutofillChip();
            return;
        }

        if (product?.name) {
            prescanProduct = product;
        } else {
            prescanProduct = null;
        }

        refreshAutofillChip();
    }

    function schedulePrescan(): void {
        hideAutofillChip();
        const upc = barcodeInput.value.trim();
        if (!looksLikeBarcode(upc)) {
            if (prescanTimer) {
                clearTimeout(prescanTimer);
                prescanTimer = null;
            }
            prescanProduct = null;
            prescanInFlight = false;
            prescanUpc = "";
            return;
        }

        if (prescanTimer) clearTimeout(prescanTimer);
        prescanUpc = upc;
        prescanProduct = null;
        prescanInFlight = false;
        prescanTimer = window.setTimeout(() => {
            prescanTimer = null;
            void runPrescan(upc);
        }, 400);
    }

    barcodeInput.addEventListener("input", schedulePrescan);
    barcodeInput.addEventListener("blur", () => refreshAutofillChip());
    if (autofillChip) {
        autofillChip.addEventListener("click", () => {
            const upc = barcodeInput.value.trim();
            if (upc && prescanProduct) void handleScannedBarcode(upc, prescanProduct);
        });
    }
    [nameInput, brandInput, categoryInput].forEach((input) => {
        input.addEventListener("input", () => refreshAutofillChip());
    });
    schedulePrescan();

    function tallyItemName(): string {
        return nameInput.value.trim() || item?.name || "item";
    }

    function refreshTallyUi(direction?: TallySlideDirection): void {
        quantity = clampTallyQuantity(quantity, minQty);
        const name = tallyItemName();
        qtyValue.classList.toggle("tally-count--empty", quantity === 0);
        updateMinusButton(minusBtn, name, quantity);

        if (direction) {
            applyTallyCountAnimation(qtyValue, quantity, direction);
        } else {
            commitTallyCount(qtyValue, quantity);
        }
    }

    minusBtn.addEventListener("click", () => {
        if (mode === "edit" && quantity <= 0 && onDeleted && item) {
            onDeleted(item);
            return;
        }
        if (quantity > minQty) {
            quantity -= 1;
            refreshTallyUi("down");
        }
    });
    form.querySelector(".tally-btn.plus")!.addEventListener("click", () => {
        quantity = clampTallyQuantity(quantity + 1, minQty);
        refreshTallyUi("up");
    });

    wireEditableTallyCount(qtyValue, {
        min: minQty,
        getQuantity: () => quantity,
        onCommit: (next) => {
            const prev = quantity;
            quantity = next;
            const name = tallyItemName();
            qtyValue.classList.toggle("tally-count--empty", quantity === 0);
            updateMinusButton(minusBtn, name, quantity);
            if (next === prev) return;
            commitTallyCount(qtyValue, prev);
            applyTallyCountAnimation(qtyValue, next, next > prev ? "up" : "down");
        },
    });

    refreshTallyUi();

    const locationPicker = createLocationPicker(locations, item?.location_id ?? undefined);
    form.querySelector("#sheet-item-location")!.appendChild(locationPicker.element);

    const photoPicker = createPhotoPicker({
        existingImageSrc: imageSrc,
        onImageChange: () => refreshAutofillChip(),
    });
    form.querySelector("#sheet-item-photo")!.appendChild(photoPicker.element);

    function fieldsMatchProduct(product: BarcodeProduct | null): boolean {
        if (!product) return false;

        const valueMatch = (productValue: string | null | undefined, inputValue: string): boolean =>
            (productValue ?? "").trim().toLowerCase() === inputValue.trim().toLowerCase();

        if (!valueMatch(product.name, nameInput.value)) return false;
        if (!valueMatch(product.brand, brandInput.value)) return false;
        if (!valueMatch(product.category, categoryInput.value)) return false;

        const productImage = resolveImageSrc(product.imageUrl) || null;
        if (photoPicker.getCleared()) return productImage === null;
        if (photoPicker.getFile()) return false;
        return productImage === imageSrc;
    }

    const scanBtn = form.querySelector(".barcode-scan-btn") as HTMLButtonElement | null;
    if (scanBtn) {
        scanBtn.addEventListener("click", () => {
            void openScannerOverlay().then((upc) => {
                if (upc) void handleScannedBarcode(upc);
            });
        });
    }

    function applyScannedData(upc: string, product: BarcodeProduct | null): void {
        const scannedName = product?.name || `Unknown (${upc})`;
        const scannedBrand = product?.brand ?? "";
        const scannedCategory = product?.category ?? "";
        const scannedImage = resolveImageSrc(product?.imageUrl) || null;

        const hasUserInput =
            nameInput.value.trim() !== "" ||
            brandInput.value.trim() !== "" ||
            categoryInput.value.trim() !== "" ||
            imageSrc !== null;

        function flashAutofilled(element: Element): void {
            element.classList.remove("is-autofilled");
            void (element as HTMLElement).offsetWidth;
            element.classList.add("is-autofilled");
            element.addEventListener("animationend", () => element.classList.remove("is-autofilled"), { once: true });
        }

        function fillValues(override: boolean): void {
            if ((override || !nameInput.value.trim()) && nameInput.value !== scannedName) {
                nameInput.value = scannedName;
                flashAutofilled(nameInput);
            }
            if ((override || !brandInput.value.trim()) && brandInput.value !== scannedBrand) {
                brandInput.value = scannedBrand;
                flashAutofilled(brandInput);
            }
            if ((override || !categoryInput.value.trim()) && categoryInput.value !== scannedCategory) {
                categoryInput.value = scannedCategory;
                flashAutofilled(categoryInput);
            }
            if (scannedImage) {
                imageSrc = scannedImage;
                photoPicker.setImageSrc(scannedImage);
                const trigger = photoPicker.element.querySelector(".photo-attach-trigger");
                if (trigger) flashAutofilled(trigger);
            } else if (override) {
                imageSrc = null;
                photoPicker.setImageSrc(null);
                const trigger = photoPicker.element.querySelector(".photo-attach-trigger");
                if (trigger) flashAutofilled(trigger);
            }
            refreshAutofillChip();
        }

        if (!hasUserInput || (!scannedName && !scannedBrand && !scannedCategory && !scannedImage)) {
            fillValues(false);
            return;
        }

        const content = document.createElement("div");
        content.className = "barcode-prompt";
        const actions = document.createElement("div");
        actions.className = "barcode-prompt-actions";
        const overrideBtn = document.createElement("button");
        overrideBtn.type = "button";
        overrideBtn.className = "btn btn-primary btn-block";
        overrideBtn.textContent = "Override all fields";
        const keepBtn = document.createElement("button");
        keepBtn.type = "button";
        keepBtn.className = "btn btn-secondary btn-block";
        keepBtn.textContent = "Keep existing, fill empty";
        actions.appendChild(overrideBtn);
        actions.appendChild(keepBtn);
        content.appendChild(actions);
        let closePrompt: () => void;
        closePrompt = showSheet("Override existing data?", content, undefined, { dismissible: true });
        overrideBtn.addEventListener("click", () => {
            closePrompt();
            fillValues(true);
        });
        keepBtn.addEventListener("click", () => {
            closePrompt();
            fillValues(false);
        });
    }

    async function handleScannedBarcode(
        upc: string,
        prefetchedProduct?: BarcodeProduct | null,
    ): Promise<void> {
        try {
            let product: BarcodeProduct | null = null;
            if (prefetchedProduct !== undefined) {
                product = prefetchedProduct;
            } else {
                try {
                    product = await api.barcode.lookup(upc);
                } catch { /* lookup failed */ }
            }

            let existingItems: Item[] = [];
            try {
                existingItems = await api.barcode.findItems(upc);
            } catch { /* lookup failed */ }

            if (mode === "edit" && item) {
                existingItems = existingItems.filter((existing) => existing.id !== item.id);
            }

            barcodeInput.value = upc;
            if (prescanTimer) {
                clearTimeout(prescanTimer);
                prescanTimer = null;
            }
            prescanUpc = upc;
            prescanProduct = product;
            prescanInFlight = false;

            const scanCandidate: Partial<Item> = {
                name: product?.name || `Unknown (${upc})`,
                brand: product?.brand ?? null,
                category: product?.category ?? null,
                barcode: upc,
                image_path: product?.imageUrl ? resolveImageSrc(product.imageUrl) : null,
                expiry_date: (form.querySelector("#sheet-item-expiry") as HTMLInputElement).value || null,
                notes: (form.querySelector("#sheet-item-notes") as HTMLTextAreaElement).value.trim() || null,
                location_id: locationPicker.getValue() ?? 0,
            };

            const similar = findSimilarItems(scanCandidate, existingItems, item?.id);
            if (similar.length > 0) {
                const content = document.createElement("div");
                content.className = "barcode-prompt";
                const message = document.createElement("p");
                message.textContent =
                    similar.length === 1
                        ? "An item with very similar attributes already exists."
                        : "Multiple similar items found.";
                content.appendChild(message);
                const actions = document.createElement("div");
                actions.className = "barcode-prompt-actions";

                for (const existing of similar) {
                    const editBtn = document.createElement("button");
                    editBtn.type = "button";
                    editBtn.className = "btn btn-primary btn-block";
                    editBtn.textContent = `Edit: ${formatItemPickerLabel(existing, similar)}`;
                    editBtn.addEventListener("click", () => {
                        closePrompt();
                        onEditExisting(existing);
                    });
                    actions.appendChild(editBtn);
                }

                const continueBtn = document.createElement("button");
                continueBtn.type = "button";
                continueBtn.className = "btn btn-secondary btn-block";
                continueBtn.textContent =
                    mode === "edit" ? "Continue editing this item" : "Continue adding new item";
                continueBtn.addEventListener("click", () => {
                    closePrompt();
                    applyScannedData(upc, product);
                });
                actions.appendChild(continueBtn);
                content.appendChild(actions);

                let closePrompt: () => void;
                closePrompt = showSheet("Similar item found", content, undefined, { dismissible: true });
                return;
            }

            applyScannedData(upc, product);
        } catch {
            /* silent failure */
        }
    }

    form.addEventListener("submit", async (e) => {
        e.preventDefault();
        setSheetError(form, null);

        const name = (form.querySelector("#sheet-item-name") as HTMLInputElement).value.trim();
        const location_id = locationPicker.getValue();
        const brand =
            (form.querySelector("#sheet-item-brand") as HTMLInputElement).value.trim() || null;
        const category =
            (form.querySelector("#sheet-item-category") as HTMLInputElement).value.trim() || null;
        const barcode =
            (form.querySelector("#sheet-item-barcode") as HTMLInputElement).value.trim() || null;
        const expiry_date =
            (form.querySelector("#sheet-item-expiry") as HTMLInputElement).value || null;
        const created_at =
            (form.querySelector("#sheet-item-added") as HTMLInputElement).value || null;
        const notes =
            (form.querySelector("#sheet-item-notes") as HTMLTextAreaElement).value.trim() || null;

        if (!name) {
            setSheetError(form, msg("toast/enter-name"));
            return;
        }
        if (!location_id) {
            setSheetError(form, msg("toast/choose-location"));
            return;
        }

        const submitBtn = form.querySelector(".item-sheet-submit") as HTMLButtonElement;
        submitBtn.disabled = true;
        const prevText = submitBtn.textContent;
        submitBtn.textContent = "Checking…";

        const payload = {
            location_id,
            name,
            brand,
            category,
            barcode,
            expiry_date,
            created_at,
            notes,
            image_path: imageSrc,
        };

        async function saveItem(): Promise<void> {
            submitBtn.disabled = true;
            submitBtn.textContent = mode === "add" ? "Preparing…" : "Updating…";
            let image_path: string | null = imageSrc;
            let pendingImage: Blob | undefined;
            const file = photoPicker.getFile();
            const cleared = photoPicker.getCleared();
            if (cleared) {
                image_path = null;
            } else if (file) {
                submitBtn.textContent = mode === "add" ? "Uploading…" : "Updating…";
                try {
                    const result = await api.upload.item(file);
                    image_path = result.path;
                } catch {
                    pendingImage = file;
                }
            }

            submitBtn.textContent = mode === "add" ? "Logging…" : "Updating…";

            try {
                if (mode === "add") {
                    const created = await api.items.create(
                        { ...payload, quantity, image_path },
                        pendingImage,
                    );
                    await onSaved(created);
                } else if (item) {
                    const updated = await api.items.update(
                        item.id,
                        { ...payload, quantity, image_path },
                        pendingImage,
                    );
                    await onSaved(updated);
                }
            } catch (err) {
                submitBtn.disabled = false;
                submitBtn.textContent = prevText ?? "";
                setSheetError(
                    form,
                    err instanceof ApiError ? err.message : msg("toast/failed-save"),
                );
            }
        }

        try {
            const existingItems = await api.items.list();
            const similar = findSimilarItems(payload, existingItems, item?.id);
            if (similar.length > 0) {
                submitBtn.disabled = false;
                submitBtn.textContent = prevText ?? "";
                showSimilarItemPrompt(
                    similar,
                    saveItem,
                    (existing) => onEditExisting(existing),
                    () => {
                        /* cancelled */
                    },
                );
                return;
            }
            await saveItem();
        } catch (err) {
            submitBtn.disabled = false;
            submitBtn.textContent = prevText ?? "";
            setSheetError(form, err instanceof ApiError ? err.message : msg("toast/failed-save"));
        }
    });
}

function escapeAttr(text: string): string {
    return text.replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function escapeHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
